import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CircleDot,
  Clock3,
  Globe2,
  KeyRound,
  Languages,
  LocateFixed,
  LogOut,
  Monitor,
  RefreshCcw,
  ShieldCheck,
  Store,
  Trash2,
  Bell,
	Send,
	Unplug,
  Eye,
  EyeOff,
  LockKeyhole,
  Mail,
  Sun,
  Boxes,
  CalendarDays,
  BarChart3,
  Activity,
} from "lucide-react";
import { z } from "zod";
import { PageContainer } from "@/components/layout/PageContainer";
import { SignOutConfirmationDialog } from "@/components/auth/SignOutConfirmationDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth, useUi, useWorkspace } from "@/lib/store";
import { languageOptions, useLocalization } from "@/lib/localization";
import { hasPermission } from "@/lib/permissions";
import { billingAccessCopy } from "@/lib/billingAccessCopy";
import {
  changeLoginPassword,
  deleteCurrentAccount,
  updateBranch,
  updateShopProfile,
  updateNotificationPreferences,
	connectTelegram,
	disconnectTelegram,
	fetchTelegramMessages,
	fetchTelegramStatus,
	sendTelegramTestMessage,
} from "@/lib/shopApi";
import { toast } from "sonner";
import { parityCopy } from "@/lib/parityLocalization";
import { requirePreciseBrowserLocation } from "@/lib/location";

const PIN_COPY = {
  en: {
    action: "Change password", cancel: "Cancel", confirm: "Confirm new password", current: "Current password",
    description: "Verify your current password, then choose a strong new password.", different: "Choose a password different from your current password.",
    failed: "Could not change your password.", fourDigits: "Use 8–72 characters with at least one letter and one number.", hide: "Hide passwords",
    mismatch: "The new passwords do not match.", new: "New password", saving: "Securing...", securityNote: "Your password is hashed and is never shown in logs or responses.",
    show: "Show passwords", success: "Password changed securely.", title: "Secure your login",
  },
};

const SECTIONS = [
  { id: "profile", labelKey: "profile", icon: Store },
  { id: "notifications", labelKey: "notificationPreferences", icon: Bell, permission: "notifications" },
  { id: "preferences", labelKey: "preferences", icon: Languages },
  { id: "branchSecurity", labelKey: "branchSecurity", icon: LocateFixed },
  { id: "sessions", labelKey: "devicesAndSync", icon: Monitor },
  { id: "account", labelKey: "account", icon: ShieldCheck },
];

const NOTIFICATION_PRESENTATION = {
  billing: {
    description: "Receive receipts, renewal reminders, and payment alerts by email",
    icon: Mail,
    iconClass: "bg-primary/10 text-primary",
    label: "Billing emails",
  },
  daily_summary: {
    description: "Receive a short sales summary the next morning",
    icon: Sun,
    iconClass: "bg-primary/10 text-primary",
    label: "Daily summary",
  },
  low_stock: {
    description: "Show alerts when products are low or out",
    icon: Boxes,
    iconClass: "bg-destructive/10 text-destructive",
    label: "Low stock alerts",
  },
  monthly_reports: {
    description: "Receive a monthly business report at the start of a new month",
    icon: CalendarDays,
    iconClass: "bg-amber-500/10 text-amber-600",
    label: "Monthly reports",
  },
  weekly_insights: {
    description: "Show weekly performance highlights",
    icon: BarChart3,
    iconClass: "bg-emerald-500/10 text-emerald-600",
    label: "Weekly insights",
  },
  analytics_alerts: {
    description: "Important trends and business insights",
    icon: Activity,
    iconClass: "bg-sky-500/10 text-sky-600",
    label: "Analytics alerts",
  },
};

const TELEGRAM_COPY = {
  ar: { connect: 'ربط', connecting: 'جارٍ الربط…', connected: 'متصل', description: 'اربط تيليجرام لإرسال واستقبال رسائل الاختبار.', disconnect: 'قطع الاتصال', disconnected: 'تم قطع اتصال تيليجرام.', disconnectFailed: 'تعذر قطع اتصال تيليجرام.', done: 'تم', email: 'البريد الإلكتروني', empty: 'أرسل رسالة إلى البوت ثم حدّث.', finish: 'افتح تيليجرام واضغط ابدأ لإكمال الربط.', messages: 'الرسائل المستلمة من تيليجرام', refresh: 'تحديث', sendFailed: 'تعذر إرسال رسالة تيليجرام.', sendTest: 'إرسال اختبار', sendTo: 'إرسال إلى', sent: 'تم إرسال رسالة الاختبار.', test: 'اختبار', testMessage: 'رسالة اختبار' },
  en: { connect: 'Connect', connecting: 'Connecting…', connected: 'Connected', description: 'Connect Telegram to send and receive test messages.', disconnect: 'Disconnect', disconnected: 'Telegram disconnected.', disconnectFailed: 'Could not disconnect Telegram.', done: 'Done', email: 'Email', empty: 'Send a message to the bot, then refresh.', finish: 'Open Telegram and press Start to finish connecting.', messages: 'Messages received from Telegram', refresh: 'Refresh', sendFailed: 'Could not send Telegram message.', sendTest: 'Send test', sendTo: 'Send to', sent: 'Test message sent to Telegram.', test: 'Test', testMessage: 'Test message' },
  fr: { connect: 'Connecter', connecting: 'Connexion…', connected: 'Connecté', description: 'Connectez Telegram pour envoyer et recevoir des messages de test.', disconnect: 'Déconnecter', disconnected: 'Telegram déconnecté.', disconnectFailed: 'Impossible de déconnecter Telegram.', done: 'Terminé', email: 'E-mail', empty: 'Envoyez un message au bot, puis actualisez.', finish: 'Ouvrez Telegram et appuyez sur Démarrer pour terminer.', messages: 'Messages reçus de Telegram', refresh: 'Actualiser', sendFailed: 'Impossible d’envoyer le message Telegram.', sendTest: 'Envoyer un test', sendTo: 'Envoyer vers', sent: 'Message test envoyé à Telegram.', test: 'Tester', testMessage: 'Message test' },
  ha: { connect: 'Haɗa', connecting: 'Ana haɗawa…', connected: 'An haɗa', description: 'Haɗa Telegram don aika da karɓar saƙonnin gwaji.', disconnect: 'Cire haɗi', disconnected: 'An cire haɗin Telegram.', disconnectFailed: 'Ba a iya cire haɗin Telegram ba.', done: 'An gama', email: 'Imel', empty: 'Aika saƙo ga bot, sannan sabunta.', finish: 'Buɗe Telegram ka danna Start don kammala haɗawa.', messages: 'Saƙonnin Telegram', refresh: 'Sabunta', sendFailed: 'Ba a iya aika saƙon Telegram ba.', sendTest: 'Aika gwaji', sendTo: 'Aika zuwa', sent: 'An aika saƙon gwaji.', test: 'Gwada', testMessage: 'Saƙon gwaji' },
  ig: { connect: 'Jikọọ', connecting: 'Na-ejikọ…', connected: 'Ejikọrọ', description: 'Jikọọ Telegram iji zipu ma nata ozi nnwale.', disconnect: 'Kwụpụ', disconnected: 'Akwụpụla Telegram.', disconnectFailed: 'Enweghị ike ịkwụpụ Telegram.', done: 'Emechaala', email: 'Email', empty: 'Zipụ ozi na bot, wee megharịa.', finish: 'Mepee Telegram pịa Start iji mechaa.', messages: 'Ozi Telegram', refresh: 'Megharịa', sendFailed: 'Enweghị ike izipu ozi Telegram.', sendTest: 'Zipụ nnwale', sendTo: 'Zipụ na', sent: 'E zigara ozi nnwale.', test: 'Nwale', testMessage: 'Ozi nnwale' },
  pt: { connect: 'Ligar', connecting: 'A ligar…', connected: 'Ligado', description: 'Ligue o Telegram para enviar e receber mensagens de teste.', disconnect: 'Desligar', disconnected: 'Telegram desligado.', disconnectFailed: 'Não foi possível desligar o Telegram.', done: 'Concluído', email: 'E-mail', empty: 'Envie uma mensagem ao bot e atualize.', finish: 'Abra o Telegram e prima Iniciar para concluir.', messages: 'Mensagens do Telegram', refresh: 'Atualizar', sendFailed: 'Não foi possível enviar a mensagem.', sendTest: 'Enviar teste', sendTo: 'Enviar para', sent: 'Mensagem de teste enviada.', test: 'Testar', testMessage: 'Mensagem de teste' },
  sw: { connect: 'Unganisha', connecting: 'Inaunganisha…', connected: 'Imeunganishwa', description: 'Unganisha Telegram kutuma na kupokea ujumbe wa majaribio.', disconnect: 'Tenganisha', disconnected: 'Telegram imetenganishwa.', disconnectFailed: 'Telegram haikuweza kutenganishwa.', done: 'Imekamilika', email: 'Barua pepe', empty: 'Tuma ujumbe kwa bot, kisha usasishe.', finish: 'Fungua Telegram ubonyeze Start kukamilisha.', messages: 'Ujumbe wa Telegram', refresh: 'Sasisha', sendFailed: 'Ujumbe haukuweza kutumwa.', sendTest: 'Tuma jaribio', sendTo: 'Tuma kwa', sent: 'Ujumbe wa jaribio umetumwa.', test: 'Jaribu', testMessage: 'Ujumbe wa jaribio' },
  yo: { connect: 'Sopọ̀', connecting: 'Ń sopọ̀…', connected: 'Ti sopọ̀', description: 'So Telegram pọ̀ láti firánṣẹ́ àti gba ìfiránṣẹ́ ìdánwò.', disconnect: 'Yọ ìsopọ̀', disconnected: 'A yọ Telegram kúrò.', disconnectFailed: 'A kò lè yọ Telegram kúrò.', done: 'Parí', email: 'Email', empty: 'Fi ìfiránṣẹ́ ránṣẹ́ sí bot, kí o sì túnṣe.', finish: 'Ṣí Telegram kí o tẹ Start láti parí.', messages: 'Ìfiránṣẹ́ Telegram', refresh: 'Túnṣe', sendFailed: 'A kò lè fi ìfiránṣẹ́ ránṣẹ́.', sendTest: 'Fi ìdánwò ránṣẹ́', sendTo: 'Fi ránṣẹ́ sí', sent: 'A fi ìfiránṣẹ́ ìdánwò ránṣẹ́.', test: 'Dánwò', testMessage: 'Ìfiránṣẹ́ ìdánwò' },
  zu: { connect: 'Xhuma', connecting: 'Iyaxhuma…', connected: 'Ixhunyiwe', description: 'Xhuma i-Telegram ukuthumela nokwamukela imilayezo yokuhlola.', disconnect: 'Nqamula', disconnected: 'I-Telegram inqanyuliwe.', disconnectFailed: 'I-Telegram ayikwazanga ukunqanyulwa.', done: 'Kwenziwe', email: 'I-imeyili', empty: 'Thumela umlayezo ku-bot, bese uvuselela.', finish: 'Vula i-Telegram ucindezele Start ukuze uqede.', messages: 'Imilayezo ye-Telegram', refresh: 'Vuselela', sendFailed: 'Umlayezo awuthunyelwanga.', sendTest: 'Thumela ukuhlola', sendTo: 'Thumela ku', sent: 'Umlayezo wokuhlola uthunyelwe.', test: 'Hlola', testMessage: 'Umlayezo wokuhlola' },
};

export default function Settings() {
  const navigate = useNavigate();
  const { language, locale, messages, setLanguage } = useLocalization();
  const copy = messages.settings;
  const parity = parityCopy[language].settings;
  const staffCopy = messages.staffPage;
  const branchesCopy = messages.branchesPage;
  const { session, logout, updateSession } = useAuth();
  const { theme, toggleTheme } = useUi();
  const {
    currentBranch,
    deleteSyncQueueItem,
    notificationPrefs,
    retryFailedSync,
    sync,
    syncQueue,
    reload,
  } = useWorkspace();
  const [active, setActive] = useState("profile");
  const [shopName, setShopName] = useState(session?.shopName ?? "");
  const [shopNameError, setShopNameError] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [updatingSecurity, setUpdatingSecurity] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [deleteValue, setDeleteValue] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [notificationDraft, setNotificationDraft] = useState(notificationPrefs);
  const [savingNotifications, setSavingNotifications] = useState(false);
	const [telegram, setTelegram] = useState({ active: false, available: true });
	const [telegramBusy, setTelegramBusy] = useState(false);
	const [telegramText, setTelegramText] = useState("Hello from Shopbook");
	const [telegramMessages, setTelegramMessages] = useState([]);
	const [showTelegramTools, setShowTelegramTools] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinForm, setPinForm] = useState({ currentPin: "", newPin: "", confirmPin: "" });
  const [pinError, setPinError] = useState("");
  const [pinVisible, setPinVisible] = useState(false);
  const [changingPin, setChangingPin] = useState(false);
  const pinCopy = PIN_COPY[language] || PIN_COPY.en;
  const telegramCopy = TELEGRAM_COPY[language] || TELEGRAM_COPY.en;

  useEffect(() => {
    setShopName(session?.shopName ?? "");
  }, [session?.shopName]);

  useEffect(() => { setNotificationDraft(notificationPrefs); }, [notificationPrefs]);

	const refreshTelegram = useCallback(async () => {
		if (!session?.sessionId) return;
		const [status, inbox] = await Promise.all([
			fetchTelegramStatus(session.sessionId),
			fetchTelegramMessages(session.sessionId),
		]);
		setTelegram(status.telegram);
		setTelegramMessages(inbox.messages ?? []);
	}, [session?.sessionId]);

	useEffect(() => {
		if (active !== "notifications" || !session?.sessionId) return undefined;
		void refreshTelegram().catch(() => {});
		const timer = window.setInterval(() => void refreshTelegram().catch(() => {}), 5000);
		return () => window.clearInterval(timer);
	}, [active, refreshTelegram, session?.sessionId]);

	async function handleTelegramConnect() {
		if (!session?.sessionId || telegramBusy) return;
		setTelegramBusy(true);
		try {
			const result = await connectTelegram(session.sessionId);
			window.open(result.telegram.connectUrl, "_blank", "noopener,noreferrer");
			toast.success(telegramCopy.finish);
		} catch (error) {
			toast.error(error?.message || telegramCopy.description);
		} finally { setTelegramBusy(false); }
	}

	async function handleTelegramDisconnect() {
		if (!session?.sessionId) return;
		setTelegramBusy(true);
		try {
			await disconnectTelegram(session.sessionId);
			await refreshTelegram();
			toast.success(telegramCopy.disconnected);
		} catch (error) { toast.error(error?.message || telegramCopy.disconnectFailed); }
		finally { setTelegramBusy(false); }
	}

	async function handleTelegramSend() {
		if (!session?.sessionId || !telegramText.trim()) return;
		setTelegramBusy(true);
		try {
			await sendTelegramTestMessage(session.sessionId, telegramText.trim());
			toast.success(telegramCopy.sent);
		} catch (error) { toast.error(error?.message || telegramCopy.sendFailed); }
		finally { setTelegramBusy(false); }
	}

  async function saveNotificationRoutes(next, changedID) {
	setNotificationDraft(next);
	setSavingNotifications(true);
	try {
	  const changed = next.find((item) => item.id === changedID);
	  await updateNotificationPreferences(session.sessionId, { routes: changed ? [{ eventType: changed.id, enabled: changed.enabled, email: changed.email, telegram: changed.telegram }] : [] });
	  toast.success(parity.updated);
	  await reload();
	} catch (error) {
	  setNotificationDraft(notificationPrefs);
	  toast.error(error?.message || parity.error);
	} finally { setSavingNotifications(false); }
  }

  async function toggleNotificationEnabled(id, enabled) {
	if (!session?.sessionId) return;
	const selected = notificationDraft.find((item) => item.id === id);
	if (enabled && selected?.telegram && !telegram.active) {
	  toast.info("Connect Telegram before turning this notification on.");
	  return;
	}
	await saveNotificationRoutes(notificationDraft.map((item) => item.id === id ? { ...item, enabled } : item), id);
  }

  async function selectNotificationChannel(id, channel) {
	if (!session?.sessionId) return;
	if (channel === "telegram" && !telegram.active) {
	  toast.info("Connect Telegram first, then choose it for this notification.");
	  await handleTelegramConnect();
	  return;
	}
	const next = notificationDraft.map((item) => item.id === id ? { ...item, email: channel === "email", telegram: channel === "telegram" } : item);
	await saveNotificationRoutes(next, id);
  }

  const selectedLanguage = languageOptions.find((option) => option.code === language) || languageOptions[0];
  const hasBranchLocation = hasSavedBranchLocation(currentBranch);
  const isAccountOwner = session?.role === "Owner";
  const branchLocationSummary = currentBranch
    ? formatBranchLocationSummary(currentBranch, copy)
    : copy.noActiveBranch;

  async function saveProfile() {
    if (!session?.sessionId) return;
    const parsed = z.object({
      shopName: z.string().trim().min(2, copy.shopNameRequired).max(120, copy.shopNameTooLong),
    }).safeParse({ shopName });
    if (!parsed.success) {
      setShopNameError(parsed.error.issues[0]?.message || copy.shopNameRequired);
      return;
    }

    setSavingProfile(true);
    setShopNameError("");
    try {
      const response = await updateShopProfile(session.sessionId, { shopName: parsed.data.shopName });
      updateSession({ ...session, ...(response?.session ?? {}), shopName: parsed.data.shopName });
      toast.success(copy.updateProfileSuccess);
    } catch (error) {
      toast.error(error?.message || copy.updateProfileFailed);
    } finally {
      setSavingProfile(false);
    }
  }

  function closePinDialog() {
    if (changingPin) return;
    setPinOpen(false);
    setPinForm({ currentPin: "", newPin: "", confirmPin: "" });
    setPinError("");
    setPinVisible(false);
  }

  async function submitPinChange(event) {
    event.preventDefault();
    if (!session?.sessionId) return;
    if (pinForm.currentPin.length < 8 || !/^(?=.*\p{L})(?=.*\p{N}).{8,72}$/u.test(pinForm.newPin)) {
      setPinError(pinCopy.fourDigits);
      return;
    }
    if (pinForm.newPin !== pinForm.confirmPin) {
      setPinError(pinCopy.mismatch);
      return;
    }
    if (pinForm.currentPin === pinForm.newPin) {
      setPinError(pinCopy.different);
      return;
    }
    setChangingPin(true);
    setPinError("");
    try {
      await changeLoginPassword(session.sessionId, pinForm);
      setPinOpen(false);
      setPinForm({ currentPin: "", newPin: "", confirmPin: "" });
      setPinVisible(false);
      toast.success(pinCopy.success);
    } catch (error) {
      setPinError(error?.message || pinCopy.failed);
    } finally {
      setChangingPin(false);
    }
  }

  async function refreshBranchLocation() {
    if (!session?.sessionId || !currentBranch?.id) return;
    setUpdatingSecurity(true);
    try {
      const location = await capturePreciseBranchBoundary();
      const response = await updateBranch(session.sessionId, currentBranch.id, { location });
      if (response?.session) {
        updateSession({ ...session, ...response.session });
      }
      await reload();
      toast.success(copy.updateLocationSuccess);
    } catch (error) {
      toast.error(error?.message || copy.updateLocationFailed);
    } finally {
      setUpdatingSecurity(false);
    }
  }

  async function confirmDeleteAccount() {
    if (!session?.sessionId) return;
    const parsed = z.object({
      confirmation: z.literal(copy.typeDelete, {
        errorMap: () => ({ message: copy.deleteAccountTypeError }),
      }),
    }).safeParse({ confirmation: deleteValue.trim().toUpperCase() });
    if (!parsed.success) {
      setDeleteError(parsed.error.issues[0]?.message || copy.deleteAccountTypeError);
      return;
    }

    setDeletingAccount(true);
    setDeleteError("");
    try {
      await deleteCurrentAccount(session.sessionId);
      toast.success(copy.deleteAccountSuccess);
      await logout();
      navigate("/");
    } catch (error) {
      toast.error(error?.message || copy.deleteAccountFailed);
    } finally {
      setDeletingAccount(false);
    }
  }

  return (
    <PageContainer>
      <div className="mx-auto max-w-6xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight">{copy.sectionTitle}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{copy.sectionDescription}</p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
          <aside className="h-fit rounded-xl border border-border/80 bg-background p-2 shadow-[0_14px_38px_rgba(15,23,42,0.055)] lg:sticky lg:top-24">
            {SECTIONS.filter((section) => hasPermission(session, section.permission)).map((section) => (
              <button
                key={section.id}
                data-testid={`settings-nav-${section.id}`}
                onClick={() => setActive(section.id)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition-colors ${
                  active === section.id
                    ? "border border-primary/20 bg-primary/10 text-primary"
                    : "border border-transparent text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                }`}
              >
                <section.icon className="h-4 w-4" />
                {copy[section.labelKey]}
              </button>
            ))}
          </aside>

          <div className="space-y-5">
            {active === "profile" ? (
              <>
                <SettingsCard title={copy.shopProfileTitle} description={copy.shopProfileDescription}>
                  <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
                    <Avatar className="h-16 w-16">
                      <AvatarFallback className="bg-primary text-lg font-bold text-primary-foreground">
                        {(session?.shopName?.slice(0, 2) || copy.shopName?.slice(0, 2) || messages.common.unknown?.slice(0, 2) || "SB").toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <LabelText>{copy.shopName}</LabelText>
                      <Input
                        data-testid="settings-shop-name"
                        value={shopName}
                        onChange={(event) => {
                          setShopName(event.target.value);
                          setShopNameError("");
                        }}
                        className={`mt-1 h-10 rounded-lg focus-visible:ring-0 focus-visible:ring-offset-0 ${shopNameError ? "border-destructive" : ""}`}
                      />
                      {shopNameError ? <p className="mt-1.5 text-xs font-medium text-destructive">{shopNameError}</p> : null}
                    </div>
                    <Button data-testid="settings-profile-save" disabled={savingProfile} onClick={saveProfile} className="h-10 rounded-lg">
                      {savingProfile ? copy.saving : copy.saveProfile}
                    </Button>
                  </div>
                </SettingsCard>

                <SettingsCard title={copy.permissions} description={copy.permissionsDescription}>
                  <div className="grid gap-3 md:grid-cols-3">
                    <InfoTile label={copy.role} value={formatRole(session?.role, branchesCopy)} />
                    <InfoTile label={copy.authMode} value={formatAuthMode(session?.authMode, copy)} />
                    <InfoTile label={copy.expires} value={session?.expiresAt ? new Date(session.expiresAt).toLocaleDateString(locale) : copy.active} />
                  </div>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {(session?.permissions ?? []).map((permission) => (
                      <Badge key={permission} variant="secondary" className="rounded-md text-[10px] uppercase tracking-wider">
                        {formatPermission(permission, staffCopy, copy, billingAccessCopy[language].label)}
                      </Badge>
                    ))}
                  </div>
                </SettingsCard>
              </>
            ) : null}

            {active === "preferences" ? (
              <>
                <SettingsCard title={copy.language} description={copy.languageDescription}>
                  <div className="grid gap-4 md:grid-cols-[1fr_280px] md:items-end">
                    <div>
                      <LabelText>{copy.selectLanguageLabel}</LabelText>
                      <Select value={language} onValueChange={(value) => void setLanguage(value)}>
                        <SelectTrigger className="mt-1 h-10 rounded-lg focus:ring-0" data-testid="settings-language-select">
                          <SelectValue placeholder={selectedLanguage.nativeLabel} />
                        </SelectTrigger>
                        <SelectContent>
                          {languageOptions.map((option) => (
                            <SelectItem key={option.code} value={option.code}>
                              {option.nativeLabel} · {option.englishLabel}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <InfoTile label={copy.current} value={selectedLanguage.englishLabel} />
                  </div>
                </SettingsCard>

                <SettingsCard title={copy.appearance} description={copy.darkModeDescription}>
                  <SwitchRow
                    icon={Globe2}
                    title={copy.darkMode}
                    description={copy.darkModeDescription}
                    checked={theme === "dark"}
                    onCheckedChange={toggleTheme}
                    testId="settings-dark-toggle"
                  />
                </SettingsCard>
              </>
            ) : null}

            {active === "notifications" ? (
              <SettingsCard title={copy.notificationPreferences} description={parity.description}>
                <div className="space-y-3">
                  {notificationDraft.map((preference) => {
                    const presentation = NOTIFICATION_PRESENTATION[preference.id] || {
                      description: preference.description,
                      icon: Bell,
                      iconClass: "bg-primary/10 text-primary",
                      label: preference.label,
                    };
                    const PreferenceIcon = presentation.icon;
                    return (
                      <div key={preference.id} className={`rounded-2xl border p-4 transition-all ${preference.enabled ? "border-primary/20 bg-card shadow-[0_8px_24px_-22px_rgba(15,23,42,0.55)]" : "border-border/70 bg-muted/25 opacity-75"}`}>
                        <div className="flex items-start gap-3">
                          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px] ${preference.enabled ? presentation.iconClass : "bg-muted text-muted-foreground"}`}><PreferenceIcon className="h-[18px] w-[18px]" /></div>
                          <div className="min-w-0 flex-1 pr-2"><div className="text-sm font-semibold">{presentation.label}</div><div className="mt-0.5 text-[11px] leading-4 text-muted-foreground">{presentation.description}</div></div>
                          <Switch disabled={savingNotifications} checked={preference.enabled} onCheckedChange={(enabled) => void toggleNotificationEnabled(preference.id, enabled)} aria-label={presentation.label} />
                        </div>
                        <div className="ml-[52px] mt-3 flex items-center gap-3">
                          <span className="shrink-0 text-[10px] text-muted-foreground">{telegramCopy.sendTo}</span>
                          <ToggleGroup
                            type="single"
                            value={preference.email ? "email" : preference.telegram ? "telegram" : ""}
                            disabled={!preference.enabled || savingNotifications}
                            onValueChange={(channel) => channel && void selectNotificationChannel(preference.id, channel)}
                            aria-label={`Delivery channel for ${presentation.label}`}
                            className="grid min-w-0 flex-1 grid-cols-2 rounded-full border border-border/70 bg-muted/70 p-1"
                          >
                            <ToggleGroupItem
                              value="email"
                              aria-label={telegramCopy.email}
                              className="h-8 rounded-full px-3 text-[11px] font-semibold text-muted-foreground transition-all data-[state=on]:!bg-primary/10 data-[state=on]:!text-primary data-[state=on]:shadow-sm data-[state=on]:ring-1 data-[state=on]:ring-primary/20"
                            >
                              <Mail className="mr-1.5 h-3.5 w-3.5" />{telegramCopy.email}
                            </ToggleGroupItem>
                            <ToggleGroupItem
                              value="telegram"
                              aria-label="Telegram"
                              disabled={telegramBusy}
                              className="h-8 rounded-full px-3 text-[11px] font-semibold text-muted-foreground transition-all data-[state=on]:!bg-[#229ED9] data-[state=on]:!text-white data-[state=on]:shadow-md data-[state=on]:ring-1 data-[state=on]:ring-[#229ED9]/30"
                            >
                              <Send className="mr-1.5 h-3.5 w-3.5" />Telegram
                            </ToggleGroupItem>
                          </ToggleGroup>
                        </div>
                      </div>
                    );
                  })}

                  <div className="border-t border-border/70 pt-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px] bg-[#229ED9]/10 text-[#229ED9]"><Send className="h-[18px] w-[18px]" /></div>
                      <div className="min-w-0 flex-1 pr-2"><div className="flex items-center gap-2 text-sm font-semibold">Telegram{telegram.active ? <span className="h-2 w-2 rounded-full bg-emerald-500" title={telegramCopy.connected} /> : null}</div><div className="mt-0.5 text-[11px] leading-4 text-muted-foreground">{telegram.active ? `${telegramCopy.connected}${telegram.chatLabel ? ` · ${telegram.chatLabel}` : ""}` : telegramCopy.description}</div></div>
                      <Button size="sm" className="rounded-full px-4" variant={telegram.active ? "outline" : "default"} disabled={telegramBusy || !telegram.available} onClick={() => telegram.active ? setShowTelegramTools((value) => !value) : handleTelegramConnect()}>{telegram.active ? (showTelegramTools ? telegramCopy.done : telegramCopy.test) : telegramBusy ? telegramCopy.connecting : telegramCopy.connect}</Button>
                    </div>
                    {telegram.active && showTelegramTools ? (
                      <div className="ml-[52px] mt-3 space-y-3 rounded-2xl bg-muted/40 p-3">
                        <Input value={telegramText} maxLength={4096} onChange={(event) => setTelegramText(event.target.value)} placeholder={telegramCopy.testMessage} className="h-10 rounded-xl bg-background" />
                        <div className="flex flex-wrap gap-2"><Button size="sm" className="min-w-28 rounded-xl" disabled={telegramBusy || !telegramText.trim()} onClick={handleTelegramSend}><Send className="mr-1.5 h-3.5 w-3.5" />{telegramCopy.sendTest}</Button><Button size="sm" variant="outline" className="rounded-xl" onClick={() => void refreshTelegram()}><RefreshCcw className="mr-1.5 h-3.5 w-3.5" />{telegramCopy.refresh}</Button><Button size="sm" variant="outline" className="rounded-xl text-destructive hover:text-destructive" disabled={telegramBusy} onClick={handleTelegramDisconnect}><Unplug className="mr-1.5 h-3.5 w-3.5" />{telegramCopy.disconnect}</Button></div>
                        <div className="pt-1 text-[11px] font-semibold">{telegramCopy.messages}</div>
                        {telegramMessages.length ? <div className="space-y-2">{telegramMessages.map((message) => <div key={message.id} className="rounded-xl border bg-background px-3 py-2"><div className="text-[10px] text-muted-foreground">{message.sender || "Telegram"} · {new Date(message.createdAt).toLocaleString(locale)}</div><div className="mt-1 text-sm">{message.text}</div></div>)}</div> : <div className="text-[11px] text-muted-foreground">{telegramCopy.empty}</div>}
                      </div>
                    ) : null}
                  </div>
                </div>
              </SettingsCard>
            ) : null}

            {active === "branchSecurity" ? (
              <SettingsCard title={copy.securityTitle} description={copy.securityDescription}>
                <div className="grid gap-4 md:grid-cols-2">
                  <InfoTile label={copy.activeBranch} value={currentBranch?.name || copy.noBranch} />
                  <InfoTile label={copy.branchLocation} value={branchLocationSummary} />
                </div>
                <div className="mt-5 space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" disabled={!isAccountOwner || updatingSecurity || !currentBranch?.id} onClick={refreshBranchLocation} data-testid="settings-refresh-branch-location">
                      <LocateFixed className="mr-1.5 h-4 w-4" /> {hasBranchLocation ? copy.updateLocationRefresh : copy.updateLocation}
                    </Button>
                  </div>
                </div>
              </SettingsCard>
            ) : null}

            {active === "sessions" ? (
              <SyncCard
                deleteSyncQueueItem={deleteSyncQueueItem}
                locale={locale}
                reload={reload}
                retryFailedSync={retryFailedSync}
                sync={sync}
                syncQueue={syncQueue}
                copy={copy}
              />
            ) : null}

            {active === "account" ? (
              <>
                <SettingsCard title={copy.pinAndSessions} description={copy.pinAndSessionsDescription}>
                  <div className="space-y-3">
                    <ActionRow icon={KeyRound} title={copy.pinTitle} description={copy.pinDescription} action={<Button variant="outline" size="sm" onClick={() => setPinOpen(true)}>{copy.changePin}</Button>} />
                    <Button
                      data-testid="settings-signout"
                      variant="outline"
                      className="w-full justify-center"
                      onClick={() => setSignOutOpen(true)}
                    >
                      <LogOut className="mr-1.5 h-4 w-4" /> {copy.signOut}
                    </Button>
                  </div>
                </SettingsCard>

                <SettingsCard title={copy.deleteAccount} description={copy.deleteAccountBody}>
                  <div className="rounded-lg border border-destructive/25 bg-destructive/5 p-4">
                    <ActionRow
                      icon={Trash2}
                      title={copy.deleteAccount}
                      description={copy.deleteAccountBody}
                      tone="danger"
                      action={<Button variant="outline" className="text-destructive hover:text-destructive" onClick={() => setDeleteOpen(true)}>{copy.deleteAccount}</Button>}
                    />
                  </div>
                </SettingsCard>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <SignOutConfirmationDialog
        branchName={currentBranch?.name}
        open={signOutOpen}
        onOpenChange={setSignOutOpen}
      />

      <Dialog open={pinOpen} onOpenChange={(open) => open ? setPinOpen(true) : closePinDialog()}>
        <DialogContent className="overflow-hidden border-primary/15 p-0 sm:max-w-[440px]">
          <div className="bg-gradient-to-br from-primary/15 via-primary/5 to-background px-6 pb-5 pt-6">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/20">
              <LockKeyhole className="h-5 w-5" />
            </div>
            <DialogHeader>
              <DialogTitle className="text-xl">{pinCopy.title}</DialogTitle>
              <DialogDescription className="leading-5">{pinCopy.description}</DialogDescription>
            </DialogHeader>
          </div>
          <form onSubmit={submitPinChange} className="space-y-4 px-6 pb-6">
            {[["currentPin", pinCopy.current], ["newPin", pinCopy.new], ["confirmPin", pinCopy.confirm]].map(([name, label]) => (
              <div key={name}>
                <LabelText>{label}</LabelText>
                <div className="relative mt-1.5">
                  <Input
                    autoComplete={name === "currentPin" ? "current-password" : "new-password"}
                    autoFocus={name === "currentPin"}
                    className="h-11 rounded-xl pr-11 text-base"
                    maxLength={72}
                    onChange={(event) => {
                      const value = event.target.value.slice(0, 72);
                      setPinForm((current) => ({ ...current, [name]: value }));
                      setPinError("");
                    }}
                    type={pinVisible ? "text" : "password"}
                    value={pinForm[name]}
                  />
                  <button type="button" aria-label={pinVisible ? pinCopy.hide : pinCopy.show} className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-muted-foreground" onClick={() => setPinVisible((value) => !value)}>
                    {pinVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            ))}
            <div className="rounded-xl border border-primary/15 bg-primary/5 px-3.5 py-3 text-xs leading-5 text-muted-foreground">
              <ShieldCheck className="mr-2 inline h-4 w-4 text-primary" />{pinCopy.securityNote}
            </div>
            {pinError ? <p role="alert" className="text-sm font-medium text-destructive">{pinError}</p> : null}
            <DialogFooter className="pt-1">
              <Button type="button" variant="ghost" disabled={changingPin} onClick={closePinDialog}>{pinCopy.cancel}</Button>
              <Button type="submit" disabled={changingPin || Object.values(pinForm).some((value) => value.length < 8)}>
                {changingPin ? pinCopy.saving : pinCopy.action}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={(open) => {
        setDeleteOpen(open);
        if (!open) {
          setDeleteValue("");
          setDeleteError("");
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{copy.deleteAccountConfirmTitle}</DialogTitle>
            <DialogDescription>{copy.deleteAccountConfirmDescription}</DialogDescription>
          </DialogHeader>
          <div>
            <LabelText>{copy.confirmation}</LabelText>
            <Input
              value={deleteValue}
              onChange={(event) => {
                setDeleteValue(event.target.value);
                setDeleteError("");
              }}
              className={`mt-1 h-10 rounded-lg focus-visible:ring-0 focus-visible:ring-offset-0 ${deleteError ? "border-destructive" : ""}`}
              placeholder={copy.typeDelete}
            />
            {deleteError ? <p className="mt-1.5 text-xs font-medium text-destructive">{deleteError}</p> : null}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)}>{copy.cancel}</Button>
            <Button variant="destructive" disabled={deletingAccount} onClick={confirmDeleteAccount}>
              {deletingAccount ? copy.deleting : copy.deleteAccountConfirmAction}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

function SettingsCard({ title, description, children }) {
  return (
    <Card className="overflow-hidden rounded-xl border-border/80 shadow-[0_14px_38px_rgba(15,23,42,0.055)]">
      <CardHeader className="border-b border-border/70 px-5 py-4">
        <CardTitle className="text-base">{title}</CardTitle>
        {description ? <CardDescription className="text-xs">{description}</CardDescription> : null}
      </CardHeader>
      <CardContent className="p-5">{children}</CardContent>
    </Card>
  );
}

function LabelText({ children }) {
  return <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{children}</div>;
}

function InfoTile({ label, value }) {
  return (
    <div className="rounded-lg border border-border/80 bg-muted/20 p-3">
      <LabelText>{label}</LabelText>
      <div className="mt-1 truncate text-sm font-semibold capitalize">{value ?? "—"}</div>
    </div>
  );
}

function SwitchRow({ icon: Icon, title, description, checked, disabled, onCheckedChange, testId }) {
  return (
    <div className="flex items-center gap-4 rounded-lg border border-border/80 bg-muted/20 p-4">
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${checked ? "bg-primary/10 text-primary" : "bg-background text-muted-foreground"}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">{title}</div>
        <div className="mt-0.5 text-xs leading-5 text-muted-foreground">{description}</div>
      </div>
      <Switch
        data-testid={testId}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        className="focus-visible:ring-0 focus-visible:ring-offset-0"
      />
    </div>
  );
}

function ActionRow({ icon: Icon, title, description, action, tone }) {
  return (
    <div className="flex items-center gap-4">
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tone === "danger" ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground"}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">{title}</div>
        <div className="mt-0.5 text-xs leading-5 text-muted-foreground">{description}</div>
      </div>
      {action}
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="py-6 text-center text-sm text-muted-foreground">{text}</div>;
}

function SyncCard({ copy, deleteSyncQueueItem, locale, reload, retryFailedSync, sync, syncQueue }) {
  return (
    <SettingsCard title={copy.syncStatus} description={copy.syncDescription}>
      <div className="flex items-center gap-3 rounded-lg border border-border/80 bg-muted/20 p-4">
        <CircleDot className={`h-3 w-3 ${sync.online ? "text-success" : "text-destructive"}`} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">
            {sync.failedCount ? copy.statusAttention : sync.queuedCount ? copy.statusPending : sync.online ? copy.statusAllCaughtUp : copy.statusOffline}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {sync.lastSyncedAt ? `${copy.lastSync} ${formatRelativeLocal(sync.lastSyncedAt, locale)}` : copy.noSyncYet} · {sync.queuedCount} {copy.pending} · {sync.failedCount} {copy.failures}
          </div>
        </div>
        {sync.failedCount > 0 ? (
          <Button variant="outline" size="sm" data-testid="settings-retry-failed-sync" onClick={async () => {
            await retryFailedSync();
            toast.success(copy.retryStarted);
          }}>
            <RefreshCcw className="mr-1.5 h-4 w-4" /> {copy.retry}
          </Button>
        ) : null}
        <Button variant="outline" size="sm" data-testid="settings-force-sync" onClick={async () => {
          await reload();
          toast.success(copy.syncRefreshed);
        }}>
          <RefreshCcw className="mr-1.5 h-4 w-4" /> {copy.forceSync}
        </Button>
      </div>

      <div className="mt-4 overflow-hidden rounded-lg border border-border/80">
        {syncQueue.length === 0 ? (
          <EmptyState text={copy.noPendingSync} />
        ) : syncQueue.slice(0, 8).map((item) => (
          <div key={item.id} className="flex items-start gap-3 border-b border-border/70 px-4 py-3 last:border-b-0" data-testid={`sync-queue-item-${item.id}`}>
            <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${item.status === "FAILED" ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground"}`}>
              {item.status === "FAILED" ? <AlertTriangle className="h-4 w-4" /> : <Clock3 className="h-4 w-4" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="truncate text-sm font-semibold">{item.itemLabel}</div>
                <Badge variant={item.status === "FAILED" ? "destructive" : "secondary"}>{formatSyncStatus(item.status, copy)}</Badge>
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {formatSyncOperation(item.operationType, copy)} · {item.detail}
              </div>
              {item.lastError ? <div className="mt-1 line-clamp-2 text-xs text-destructive">{item.lastError}</div> : null}
            </div>
            {item.canDelete ? (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                data-testid={`sync-queue-delete-${item.id}`}
                onClick={async () => {
                  try {
                    await deleteSyncQueueItem(item.id);
                    toast.success(copy.removeQueuedSuccess);
                  } catch (error) {
                    toast.error(error?.message || copy.removeQueuedFailed);
                  }
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        ))}
      </div>
    </SettingsCard>
  );
}

function formatBranchLocationSummary(branch, copy) {
  if (hasSavedBranchLocation(branch)) {
    return `${Number(branch.declaredLatitude).toFixed(5)}, ${Number(branch.declaredLongitude).toFixed(5)}`;
  }
  return copy.noBranchLocation;
}

function hasSavedBranchLocation(branch) {
  return branch?.declaredLatitude !== null
    && branch?.declaredLatitude !== undefined
    && branch?.declaredLongitude !== null
    && branch?.declaredLongitude !== undefined;
}

async function capturePreciseBranchBoundary() {
  const location = await requirePreciseBrowserLocation();
  return {
    ...location,
    declaredLatitude: location.latitude,
    declaredLongitude: location.longitude,
    locationSource: "device",
  };
}

function formatSyncOperation(operationType, copy) {
  const labels = {
    DEBT_PAYMENT_RECORDED: copy.operationDebtPayment,
    DEBT_RECORDED: copy.operationDebt,
    PRODUCT_UPSERT: copy.operationProduct,
    SALE_RECORDED: copy.operationSale,
    STOCK_MOVEMENT_RECORDED: copy.operationStock,
  };
  return labels[operationType] || operationType;
}

function formatRelativeLocal(iso, locale) {
  if (!iso) return "—";
  const date = new Date(iso);
  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const abs = Math.abs(seconds);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  if (abs < 60) return rtf.format(Math.round(seconds), "second");
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(seconds / 3600), "hour");
  if (abs < 7 * 86400) return rtf.format(Math.round(seconds / 86400), "day");

  return date.toLocaleDateString(locale, { day: "2-digit", month: "short" });
}

function formatRole(role, branchesCopy) {
  const normalized = role === "Owner" ? "Admin" : role;
  const map = {
    Admin: branchesCopy.roleAdmin,
    Member: branchesCopy.roleMember,
    Owner: branchesCopy.roleOwner,
  };
  return map[normalized] ?? normalized ?? "—";
}

function formatAuthMode(authMode, copy) {
  const map = {
    independent: copy.authModeIndependent,
    session: copy.authModeSession,
    staff_setup: copy.authModeStaffSetup,
  };
  return map[authMode] ?? authMode ?? copy.session;
}

function formatPermission(permission, staffCopy, copy, billingLabel) {
  const map = {
    sell: staffCopy.permissionSell,
    history: staffCopy.permissionHistory,
    inventory: staffCopy.permissionInventory,
    debts: staffCopy.permissionDebts,
    expenses: staffCopy.permissionExpenses,
    analytics: staffCopy.permissionAnalytics,
    notifications: staffCopy.permissionNotifications,
    staff_access: staffCopy.permissionStaffAccess,
    branch_management: staffCopy.permissionBranchManagement,
    settings: copy.permissionSettings,
    assistant: copy.permissionAssistant,
    billing: billingLabel,
  };
  return map[permission] ?? permission.replaceAll("_", " ");
}

function formatSyncStatus(status, copy) {
  const map = {
    FAILED: copy.syncFailed,
    PENDING: copy.syncPendingShort,
    QUEUED: copy.syncQueued,
    SYNCED: copy.syncSynced,
  };
  return map[status] ?? status;
}
